import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { contentAPI } from '../services/api';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, UnderlineType } from 'docx';
import { saveAs } from 'file-saver';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { checkAuthBeforeAction } from '../utils/authHelper';
import NavigationWarningModal from '../components/NavigationWarningModal';
import usePermission from '../hooks/usePermission';
import UpgradePrompt from '../components/UpgradePrompt';

const MarketSurvey = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Permission check
  const { hasFeatureAccess, getPlanInfo, getRequiredPlanForFeature } = usePermission();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptData, setUpgradePromptData] = useState({});

  // Form states
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [competitors, setCompetitors] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');

  // UI states
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  const navigationWarning = useNavigationWarning(generating, 'A market research report is being generated. If you leave now, the generation will continue in the background. Are you sure you want to leave?', 'content-generation');

  const productCategories = [
    { value: '', label: 'Select a category' },
    { value: 'consumer-electronics', label: 'Consumer Electronics' },
    { value: 'fashion-apparel', label: 'Fashion & Apparel' },
    { value: 'food-beverage', label: 'Food & Beverage' },
    { value: 'health-wellness', label: 'Health & Wellness' },
    { value: 'home-garden', label: 'Home & Garden' },
    { value: 'beauty-cosmetics', label: 'Beauty & Cosmetics' },
    { value: 'sports-outdoors', label: 'Sports & Outdoors' },
    { value: 'toys-games', label: 'Toys & Games' },
    { value: 'automotive', label: 'Automotive' },
    { value: 'software-saas', label: 'Software & SaaS' },
    { value: 'education', label: 'Education' },
    { value: 'other', label: 'Other' }
  ];

  const targetMarkets = [
    { value: '', label: 'Select a target market' },
    { value: 'north-america', label: 'North America' },
    { value: 'europe', label: 'Europe' },
    { value: 'asia-pacific', label: 'Asia-Pacific' },
    { value: 'latin-america', label: 'Latin America' },
    { value: 'middle-east-africa', label: 'Middle East & Africa' },
    { value: 'global', label: 'Global Market' }
  ];

  // Check if there's a saved report to display
  useEffect(() => {
    if (location.state?.savedReport) {
      const saved = location.state.savedReport;
      setReport(saved);
      // Set all form fields from saved data
      if (saved.productName) setProductName(saved.productName);
      if (saved.productDescription) setProductDescription(saved.productDescription);
      if (saved.productCategory) setProductCategory(saved.productCategory);
      if (saved.targetMarket) setTargetMarket(saved.targetMarket);

      // Handle competitors - convert array to comma-separated string
      if (saved.competitors) {
        if (Array.isArray(saved.competitors)) {
          setCompetitors(saved.competitors.join(', '));
        } else if (typeof saved.competitors === 'string') {
          setCompetitors(saved.competitors);
        }
      }

      if (saved.additionalInfo) setAdditionalInfo(saved.additionalInfo);
    }
  }, [location.state]);

  const handleGenerateReport = async () => {
    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    // Validation
    if (!productName.trim()) {
      setError('Please enter a product name');
      return;
    }

    if (!productDescription.trim()) {
      setError('Please enter a product description');
      return;
    }

    if (!productCategory) {
      setError('Please select a product category');
      return;
    }

    if (!targetMarket) {
      setError('Please select a target market');
      return;
    }

    // Check feature permission
    if (!hasFeatureAccess('market-survey')) {
      const planInfo = getPlanInfo();
      setUpgradePromptData({
        type: 'feature',
        featureName: 'Market Research',
        currentPlan: planInfo.name,
        requiredPlan: getRequiredPlanForFeature('market-survey')
      });
      setShowUpgradePrompt(true);
      return;
    }

    setGenerating(true);
    setError('');
    setProgress(0);
    setCurrentStep('Initializing market research...');

    try {
      // Step 1: Prepare research data
      setProgress(10);
      setCurrentStep('Gathering market data...');

      const researchData = {
        productName,
        productDescription,
        productCategory,
        targetMarket,
        competitors: competitors.trim() ? competitors.split(',').map(c => c.trim()) : [],
        additionalInfo: additionalInfo.trim()
      };

      // Step 2: Call market survey API
      setProgress(30);
      setCurrentStep('Analyzing market trends...');

      const response = await contentAPI.generateMarketSurvey(researchData);

      setProgress(60);
      setCurrentStep('Compiling competitive analysis...');

      // Simulate progress for better UX
      await new Promise(resolve => setTimeout(resolve, 1000));

      setProgress(80);
      setCurrentStep('Generating final report...');

      await new Promise(resolve => setTimeout(resolve, 1000));

      setProgress(100);
      setCurrentStep('Complete!');

      if (response.data.success) {
        const reportData = response.data.data;

        // Debug: Log the report data structure
        console.log('📊 Market Survey Report Data:', reportData);
        console.log('📋 Report sections:', Object.keys(reportData));
        console.log('🔍 strategyAndLaunch exists?', !!reportData.strategyAndLaunch);
        console.log('🔍 implementationRoadmap exists?', !!reportData.implementationRoadmap);

        if (reportData.strategyAndLaunch) {
          console.log('✅ strategyAndLaunch length:', reportData.strategyAndLaunch.length);
        }
        if (reportData.implementationRoadmap) {
          console.log('✅ implementationRoadmap length:', reportData.implementationRoadmap.length);
        }

        setReport(reportData);
      } else {
        throw new Error(response.data.error?.message || 'Failed to generate report');
      }
    } catch (err) {
      console.error('Market survey error:', err);
      setError(err.response?.data?.error?.message || err.message || 'Failed to generate market survey report');
      setCurrentStep('');
    } finally {
      setGenerating(false);
    }
  };

  const handleReset = () => {
    setProductName('');
    setProductDescription('');
    setProductCategory('');
    setTargetMarket('');
    setCompetitors('');
    setAdditionalInfo('');
    setReport(null);
    setError('');
    setProgress(0);
    setCurrentStep('');
  };

  const handleExportWord = async () => {
    if (!report) return;

    try {
      // Create document sections
      const sections = [];

      // Add title
      sections.push(
        new Paragraph({
          text: 'Market Research Report',
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 }
        })
      );

      // Add product name
      sections.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Product: ${productName}`,
              bold: true,
              size: 24
            })
          ],
          spacing: { after: 200 }
        })
      );

      // Add generated date
      sections.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Generated on: ${new Date().toLocaleString()}`,
              italics: true,
              size: 20
            })
          ],
          spacing: { after: 600 }
        })
      );

      // Helper function to add section
      const addSection = (title, content) => {
        if (content) {
          // Add section title
          sections.push(
            new Paragraph({
              text: title,
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            })
          );

          // Split content into paragraphs
          const paragraphs = content.split('\n\n');
          paragraphs.forEach(para => {
            if (para.trim()) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: para.trim(),
                      size: 22
                    })
                  ],
                  spacing: { after: 200 },
                  alignment: AlignmentType.JUSTIFIED
                })
              );
            }
          });
        }
      };

      // Add all report sections
      addSection('Executive Summary', report.executiveSummary);
      addSection('Market Overview', report.marketOverview);
      addSection('Competitive Analysis', report.competitiveAnalysis);
      addSection('Market Trends', report.marketTrends);
      addSection('Opportunities', report.opportunities);
      addSection('Challenges & Risks', report.challenges);
      addSection('Market Strategy & Launch Plan', report.strategyAndLaunch);
      addSection('Implementation Roadmap', report.implementationRoadmap);
      addSection('Conclusion', report.conclusion);

      // Add references section
      if (report.references && report.references.length > 0) {
        sections.push(
          new Paragraph({
            text: 'References',
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 }
          })
        );

        report.references.forEach((ref, index) => {
          sections.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `[${index + 1}] `,
                  bold: true,
                  size: 22
                }),
                new TextRun({
                  text: ref.title,
                  size: 22
                })
              ],
              spacing: { after: 100 }
            })
          );

          sections.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: ref.url,
                  size: 20,
                  italics: true,
                  color: '0563C1',
                  underline: {
                    type: UnderlineType.SINGLE
                  }
                })
              ],
              spacing: { after: 100 }
            })
          );

          sections.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Search: ${ref.query}`,
                  size: 18,
                  italics: true,
                  color: '666666'
                })
              ],
              spacing: { after: 200 }
            })
          );
        });
      }

      // Create document
      const doc = new Document({
        sections: [{
          properties: {},
          children: sections
        }]
      });

      // Generate and download
      const blob = await Packer.toBlob(doc);

      // Format filename for Western markets: lowercase with hyphens, date in YYYY-MM-DD format
      const productSlug = productName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')  // Replace non-alphanumeric with hyphens
        .replace(/^-+|-+$/g, '');      // Remove leading/trailing hyphens

      const dateStr = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const fileName = `market-research-${productSlug}-${dateStr}.docx`;

      saveAs(blob, fileName);

    } catch (error) {
      console.error('Error exporting Word document:', error);
      setError('Failed to export Word document. Please try again.');
    }
  };

  return (
    <>
      <NavigationWarningModal
        isOpen={navigationWarning.showModal}
        onCancel={navigationWarning.handleCancelLeave}
        onConfirm={navigationWarning.handleConfirmLeave}
        message={navigationWarning.warningMessage}
        taskType={navigationWarning.taskType}
      />

      <UpgradePrompt
        show={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        type={upgradePromptData.type}
        featureName={upgradePromptData.featureName}
        currentPlan={upgradePromptData.currentPlan}
        requiredPlan={upgradePromptData.requiredPlan}
      />

      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="Market Survey" />
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-6">
            {/* Page Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Product Market Research</h1>
              <p className="text-gray-600">
                Generate comprehensive market research reports powered by AI. Our system analyzes market trends,
                competitor landscape, and provides actionable insights for your product.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input Form */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Product Information</h2>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-4">
                  {/* Product Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Name *
                    </label>
                    <input
                      type="text"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder="e.g., Smart Fitness Watch Pro"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={generating}
                    />
                  </div>

                  {/* Product Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Description *
                    </label>
                    <textarea
                      value={productDescription}
                      onChange={(e) => setProductDescription(e.target.value)}
                      placeholder="Describe your product, its key features, and unique selling points..."
                      rows="4"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      disabled={generating}
                    />
                  </div>

                  {/* Product Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Category *
                    </label>
                    <select
                      value={productCategory}
                      onChange={(e) => setProductCategory(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={generating}
                    >
                      {productCategories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Target Market */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Market *
                    </label>
                    <select
                      value={targetMarket}
                      onChange={(e) => setTargetMarket(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={generating}
                    >
                      {targetMarkets.map(market => (
                        <option key={market.value} value={market.value}>{market.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Competitors */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Known Competitors (Optional)
                    </label>
                    <input
                      type="text"
                      value={competitors}
                      onChange={(e) => setCompetitors(e.target.value)}
                      placeholder="Competitor 1, Competitor 2, Competitor 3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={generating}
                    />
                    <p className="mt-1 text-xs text-gray-500">Separate multiple competitors with commas</p>
                  </div>

                  {/* Additional Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Information (Optional)
                    </label>
                    <textarea
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                      placeholder="Any specific areas you want to focus on, price range, target demographics, etc."
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      disabled={generating}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleGenerateReport}
                      disabled={generating}
                      className="flex-1 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      {generating ? 'Generating Report...' : 'Generate Market Report'}
                    </button>
                    {(report || generating) && (
                      <button
                        onClick={handleReset}
                        disabled={generating}
                        className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Progress & Results */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Research Progress</h2>

                {!generating && !report && (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                    <svg className="w-20 h-20 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <p className="text-sm">Fill in the product information and click "Generate Market Report" to start</p>
                  </div>
                )}

                {generating && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">{currentStep}</span>
                      <span className="text-primary-600 font-semibold">{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-primary-500 to-primary-600 h-full transition-all duration-500 ease-out"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        <span className="font-semibold">Note:</span> This process may take 3-10 minutes depending on the depth of analysis required.
                      </p>
                    </div>
                  </div>
                )}

                {report && !generating && (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                      <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="font-semibold text-green-800">Report Generated Successfully!</p>
                        <p className="text-sm text-green-700">Scroll down to view the full market research report</p>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    {report.summary && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-xs text-blue-600 font-medium">Market Size</p>
                          <p className="text-lg font-bold text-blue-900">{report.summary.marketSize || 'N/A'}</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3">
                          <p className="text-xs text-purple-600 font-medium">Growth Rate</p>
                          <p className="text-lg font-bold text-purple-900">{report.summary.growthRate || 'N/A'}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3">
                          <p className="text-xs text-green-600 font-medium">Competitors</p>
                          <p className="text-lg font-bold text-green-900">{report.summary.competitorCount || 0}</p>
                        </div>
                        <div className="bg-orange-50 rounded-lg p-3">
                          <p className="text-xs text-orange-600 font-medium">Market Trend</p>
                          <p className="text-lg font-bold text-orange-900">{report.summary.trend || 'N/A'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Full Report */}
            {report && !generating && (
              <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Market Research Report</h2>
                  <button
                    onClick={handleExportWord}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export to Word
                  </button>
                </div>

                <div className="prose max-w-none">
                  {/* Executive Summary */}
                  {report.executiveSummary && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-primary-500">
                        Executive Summary
                      </h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">{report.executiveSummary}</p>
                    </div>
                  )}

                  {/* Market Overview */}
                  {report.marketOverview && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-primary-500">
                        Market Overview
                      </h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">{report.marketOverview}</p>
                    </div>
                  )}

                  {/* Competitive Analysis */}
                  {report.competitiveAnalysis && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-primary-500">
                        Competitive Analysis
                      </h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">{report.competitiveAnalysis}</p>
                    </div>
                  )}

                  {/* Market Trends */}
                  {report.marketTrends && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-primary-500">
                        Market Trends
                      </h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">{report.marketTrends}</p>
                    </div>
                  )}

                  {/* Opportunities */}
                  {report.opportunities && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-green-500">
                        Opportunities
                      </h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">{report.opportunities}</p>
                    </div>
                  )}

                  {/* Challenges */}
                  {report.challenges && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-red-500">
                        Challenges & Risks
                      </h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">{report.challenges}</p>
                    </div>
                  )}

                  {/* Market Strategy & Launch Plan */}
                  {report.strategyAndLaunch && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-purple-500">
                        Market Strategy & Launch Plan
                      </h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">{report.strategyAndLaunch}</p>
                    </div>
                  )}

                  {/* Implementation Roadmap */}
                  {report.implementationRoadmap && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-indigo-500">
                        Implementation Roadmap
                      </h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">{report.implementationRoadmap}</p>
                    </div>
                  )}

                  {/* Conclusion */}
                  {report.conclusion && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-primary-500">
                        Conclusion
                      </h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">{report.conclusion}</p>
                    </div>
                  )}

                  {/* References */}
                  {report.references && report.references.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-blue-500">
                        References
                      </h3>
                      <div className="space-y-3">
                        {report.references.map((ref, index) => (
                          <div key={index} className="flex gap-3">
                            <span className="font-bold text-primary-600 flex-shrink-0">[{index + 1}]</span>
                            <div className="flex-1">
                              <a
                                href={ref.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-700 hover:underline font-medium break-all block"
                              >
                                {ref.title}
                              </a>
                              <p className="text-xs text-gray-500 mt-1">
                                Search: <span className="italic">{ref.query}</span>
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-4 italic">
                        * Citations in the report (e.g., [1], [2]) correspond to the numbered references above.
                      </p>
                    </div>
                  )}

                  {/* Timestamp */}
                  <div className="mt-8 pt-6 border-t border-gray-200 text-sm text-gray-500">
                    <p>Report generated on {new Date().toLocaleString()}</p>
                    <p className="mt-1">Product: {productName}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default MarketSurvey;
